/**
 * E2E Encryption Service using TweetNaCl.js
 * Implements true end-to-end encryption where only Users A and B can read messages
 * Firebase/Google/admins cannot decrypt (no plaintext keys stored on server)
 */

import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { db } from '../firebase';
import debugLogger from '../utils/debugLogger';
import encryptionErrorHandler from '../utils/encryptionErrorHandler';

const PUBLIC_KEYS_COLLECTION = 'userPublicKeys';

/**
 * Initialize user's keypair (run once during account setup)
 * NEVER send private key to server - store locally only in IndexedDB
 */
export const generateUserKeypair = async () => {
  const keypair = nacl.box.keyPair();

  const publicKeyBase64 = naclUtil.encodeBase64(keypair.publicKey);
  const secretKeyBase64 = naclUtil.encodeBase64(keypair.secretKey);

  // Keypair generation successful - no logging for security
  return {
    publicKey: publicKeyBase64,
    secretKey: secretKeyBase64,
  };
};

/**
 * Store private key locally in IndexedDB (browser only, never sent to server)
 * Now supports device-specific keys to prevent cross-device key conflicts
 * 
 * Key naming convention:
 * - Device-specific: `${userId}_${deviceId}_secretKey`
 * - Legacy fallback: `${userId}_secretKey` (for backward compatibility)
 */
export const storePrivateKeyLocally = async (userId, secretKey, deviceId = null) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('e2eEncryption', 2);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('keys')) {
        db.createObjectStore('keys');
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');

      // If deviceId is provided, store with device context (NEW)
      const keyName = deviceId
        ? `${userId}_${deviceId}_secretKey`
        : `${userId}_secretKey`;

      store.put(secretKey, keyName);

      transaction.oncomplete = () => {
        db.close();
        resolve(true);
      };
      transaction.onerror = () => reject(transaction.error);
    };
  });
};

/**
 * Retrieve private key from IndexedDB (only available locally)
 * Now device-aware: tries device-specific key first, then falls back to legacy key
 * 
 * Priority order:
 * 1. Device-specific key: `${userId}_${deviceId}_secretKey`
 * 2. Legacy key (backward compatibility): `${userId}_secretKey`
 */
export const getPrivateKeyLocally = async (userId, deviceId = null) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('e2eEncryption', 2);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('keys')) {
        db.createObjectStore('keys');
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['keys'], 'readonly');
      const store = transaction.objectStore('keys');

      // First, try to get device-specific key if deviceId is provided
      const deviceSpecificKeyName = deviceId
        ? `${userId}_${deviceId}_secretKey`
        : null;

      if (deviceSpecificKeyName) {
        const deviceSpecificRequest = store.get(deviceSpecificKeyName);

        deviceSpecificRequest.onsuccess = () => {
          const secretKey = deviceSpecificRequest.result;

          if (secretKey) {
            db.close();
            debugLogger.log('✅ Device-specific private key retrieved from IndexedDB');
            debugLogger.log('  Key length:', secretKey.length);
            resolve(secretKey);
            return;
          }

          // Device-specific key not found, try legacy key for backward compatibility
          const legacyRequest = store.get(`${userId}_secretKey`);

          legacyRequest.onsuccess = () => {
            const legacySecretKey = legacyRequest.result;
            if (legacySecretKey) {
              db.close();
              debugLogger.log('⚠️ Using legacy private key (device-specific not found)');
              debugLogger.log('  Key length:', legacySecretKey.length);
              resolve(legacySecretKey);
            } else {
              db.close();
              resolve(null);
            }
          };

          legacyRequest.onerror = () => {
            db.close();
            resolve(null);
          };
        };

        deviceSpecificRequest.onerror = () => {
          db.close();
          resolve(null);
        };
      } else {
        // No deviceId provided, fall back to legacy key lookup
        const legacyRequest = store.get(`${userId}_secretKey`);

        legacyRequest.onsuccess = () => {
          const secretKey = legacyRequest.result;
          if (secretKey) {
            debugLogger.log('✅ Private key retrieved from IndexedDB (legacy):');
            debugLogger.log('  Key length:', secretKey.length);
            // User ID context
          } else {
            // Private key not found in IndexedDB
          }
          db.close();
          resolve(secretKey || null);
        };
        legacyRequest.onerror = () => {
          db.close();
          resolve(null);
        };
      }
    };
  });
};

/**
 * Get recipient's public key from Firestore
 * Public keys CAN be stored on server (they're public, not secret)
 */
export const getRecipientPublicKey = async (userId, getDocFn, collectionFn, docFn) => {
  try {
    const docRef = docFn(collectionFn(db), PUBLIC_KEYS_COLLECTION, userId);
    const docSnap = await getDocFn(docRef);

    if (docSnap.exists()) {
      return docSnap.data().publicKey;
    }
    return null;
  } catch (error) {
    debugLogger.error(new Error('Error fetching public key: ' + (error?.message || 'unknown')));
    encryptionErrorHandler.handleKeyError(error);
    return null;
  }
};

/**
 * Encrypt message for recipient (only they can decrypt)
 * Uses recipient's public key + user's private key
 */
export const encryptMessageForRecipient = async (
  message,
  recipientPublicKeyBase64,
  userSecretKeyBase64
) => {
  try {
    // Decode keys from base64
    const recipientPublicKey = naclUtil.decodeBase64(recipientPublicKeyBase64);
    const userSecretKey = naclUtil.decodeBase64(userSecretKeyBase64);

    // Debug logging

    // Generate nonce (unique per message)
    const nonce = nacl.randomBytes(nacl.box.nonceLength);

    // Encrypt message using recipient's public key + user's secret key
    const messageUint8 = naclUtil.decodeUTF8(JSON.stringify(message));
    const encrypted = nacl.box(messageUint8, nonce, recipientPublicKey, userSecretKey);

    // Return encrypted message + nonce (both needed for decryption)
    return {
      ciphertext: naclUtil.encodeBase64(encrypted),
      nonce: naclUtil.encodeBase64(nonce),
      senderPublicKey: null, // Recipient already knows this from conversation
    };
  } catch (error) {
    encryptionErrorHandler.handleEncryptionError(error, 'encryptMessageForRecipient');
    throw new Error('Failed to encrypt message');
  }
};

/**
 * Encrypt message for self (sender can view their own messages only on this device)
 * Uses sender's own public key + private key
 * ✅ NEW: Enables device-scoped privacy - messages only visible on device with matching keys
 */
export const encryptMessageForSelf = async (
  message,
  senderPublicKeyBase64,
  senderSecretKeyBase64
) => {
  try {
    // Decode keys from base64
    const senderPublicKey = naclUtil.decodeBase64(senderPublicKeyBase64);
    const senderSecretKey = naclUtil.decodeBase64(senderSecretKeyBase64);

    // Generate nonce (unique per message)
    const nonce = nacl.randomBytes(nacl.box.nonceLength);

    // Encrypt message using sender's own public key + secret key
    const messageUint8 = naclUtil.decodeUTF8(JSON.stringify(message));
    const encrypted = nacl.box(messageUint8, nonce, senderPublicKey, senderSecretKey);

    // Return encrypted message + nonce
    return {
      ciphertext: naclUtil.encodeBase64(encrypted),
      nonce: naclUtil.encodeBase64(nonce),
    };
  } catch (error) {
    encryptionErrorHandler.handleEncryptionError(error, 'encryptMessageForSelf');
    throw new Error('Failed to encrypt message for self');
  }
};


/**
 * Decrypt message (only recipient with private key can decrypt)
 * Uses sender's public key + recipient's private key
 */
export const decryptMessage = async (
  encryptedData,
  senderPublicKeyBase64,
  recipientSecretKeyBase64
) => {
  try {
    // Decode keys and ciphertext from base64
    const senderPublicKey = naclUtil.decodeBase64(senderPublicKeyBase64);
    const recipientSecretKey = naclUtil.decodeBase64(recipientSecretKeyBase64);
    const ciphertext = naclUtil.decodeBase64(encryptedData.ciphertext);
    const nonce = naclUtil.decodeBase64(encryptedData.nonce);

    // Decrypt message
    const decrypted = nacl.box.open(
      ciphertext,
      nonce,
      senderPublicKey,
      recipientSecretKey
    );

    if (!decrypted) {
      throw new Error('Decryption failed - message tampered with or invalid keys');
    }

    // Parse decrypted message
    const messageString = naclUtil.encodeUTF8(decrypted);
    return JSON.parse(messageString);
  } catch (error) {
    encryptionErrorHandler.handleDecryptionError(error, 'unknown_id');
    throw new Error('Failed to decrypt message');
  }
};

/**
 * Upload public key to Firestore (safe to do - it's public)
 * Private key NEVER leaves the user's device
 */
export const uploadPublicKeyToFirebase = async (userId, publicKey, db, serverTimestamp) => {
  try {
    await db.collection(PUBLIC_KEYS_COLLECTION).doc(userId).set(
      {
        userId,
        publicKey,
        uploadedAt: serverTimestamp(),
        // Never store private key here
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    console.error('Error uploading public key:', error);
    return false;
  }
};

/**
 * Create encrypted message object for Firestore storage
 * Message content is FULLY ENCRYPTED - Firebase sees only ciphertext
 * 
 * NOTE: Import serverTimestamp from firebase/firestore where this is called
 */
export const createEncryptedMessagePayload = (
  messageId,
  userId,
  recipientId,
  encryptedContent,
  messageType = 'text',
  originalMessageText = null,
  serverTimestampFn = null, // Pass serverTimestamp() function from caller
  senderPublicKey = null // ✅ NEW: Pass sender's public key for robust decryption
) => {
  return {
    senderId: userId,
    recipientId,
    encrypted: true,
    encryptedContent: {
      ciphertext: encryptedContent.ciphertext,
      nonce: encryptedContent.nonce,
    },
    messageType,
    createdAt: new Date(), // Keep for backwards compatibility
    // ✅ FIX: Use server timestamp to eliminate clock skew between users
    // Messages will be ordered by server time, not user's system time
    timestamp: serverTimestampFn ? serverTimestampFn() : new Date(), // Fallback to client if not provided
    clientTimestamp: new Date(), // Keep for reference/debugging
    // Store original message text so sender can see their own messages (they already know the content)
    // Recipient will decrypt from encryptedContent
    originalMessage: originalMessageText,
    // ✅ NEW: Store sender's public key directly in message
    // This allows recipient to decrypt even if sender changes devices/keys later
    senderPublicKey: senderPublicKey,
  };
};

/**
 * Verify message integrity (HMAC verification)
 * Ensures message wasn't tampered with in transit
 */
export const generateMessageHMAC = (message, secretKey) => {
  // Use a simple HMAC for integrity verification
  const encoder = new TextEncoder();
  const data = encoder.encode(JSON.stringify(message) + secretKey);

  return crypto.subtle.digest('SHA-256', data).then(hashBuffer => {
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  });
};

/**
 * Test function to verify that a generated keypair can encrypt and decrypt messages
 * Use this to debug encryption issues
 */
export const testE2EEncryption = async () => {
  try {
    // Generate a test keypair
    const testKeypair = nacl.box.keyPair();
    const publicKeyBase64 = naclUtil.encodeBase64(testKeypair.publicKey);
    const secretKeyBase64 = naclUtil.encodeBase64(testKeypair.secretKey);

    // Test message
    const testMessage = {
      text: 'Test encryption message',
      timestamp: new Date().toISOString(),
      senderId: 'test-user-1',
    };

    // Encrypt with public key + secret key (as if sender is encrypting for themselves)
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const publicKey = naclUtil.decodeBase64(publicKeyBase64);
    const secretKey = naclUtil.decodeBase64(secretKeyBase64);
    const messageUint8 = naclUtil.decodeUTF8(JSON.stringify(testMessage));
    const encrypted = nacl.box(messageUint8, nonce, publicKey, secretKey);
    const ciphertext = naclUtil.encodeBase64(encrypted);
    const nonceBase64 = naclUtil.encodeBase64(nonce);

    // Now decrypt (using public key + secret key)
    const decryptedCiphertext = naclUtil.decodeBase64(ciphertext);
    const decryptedNonce = naclUtil.decodeBase64(nonceBase64);
    const decrypted = nacl.box.open(
      decryptedCiphertext,
      decryptedNonce,
      publicKey,
      secretKey
    );

    if (!decrypted) {
      console.error('❌ Round-trip decryption FAILED - message tampered with');
      return false;
    }

    const decryptedMessage = JSON.parse(naclUtil.encodeUTF8(decrypted));

    if (decryptedMessage.text === testMessage.text) {
      return true;
    } else {
      console.error('❌ Decrypted message doesn\'t match original');
      return false;
    }
  } catch (error) {
    console.error('❌ E2E encryption test failed:', error);
    return false;
  }
};

/**
 * Verify that a public/secret key pair from storage actually work together
 * This catches the bug where keys are out of sync between IndexedDB and Firestore
 */
export const verifyKeypairIntegrity = async (userId, storedPublicKey, storedSecretKey) => {
  try {
    // Decode both keys from base64
    const publicKey = naclUtil.decodeBase64(storedPublicKey);
    const secretKey = naclUtil.decodeBase64(storedSecretKey);

    // The public key should always be derivable from the secret key
    // For NaCl box, both keys should be 32 bytes
    if (publicKey.length !== 32 || secretKey.length !== 32) {
      console.error('❌ Invalid key lengths:');
      console.error('   Public key:', publicKey.length, 'bytes (expected 32)');
      console.error('   Secret key:', secretKey.length, 'bytes (expected 32)');
      return false;
    }

    // Test: Encrypt a message with public key + secret key
    const testMsg = naclUtil.decodeUTF8('integrity-test');
    const testNonce = nacl.randomBytes(nacl.box.nonceLength);
    const encrypted = nacl.box(testMsg, testNonce, publicKey, secretKey);

    // Try to decrypt with the same key pair
    const decrypted = nacl.box.open(encrypted, testNonce, publicKey, secretKey);

    if (!decrypted) {
      console.error('❌ KEYPAIR MISMATCH: Cannot decrypt with stored key pair!');
      console.error('   This means the public key in Firestore doesn\'t match the secret key in IndexedDB');
      return false;
    }

    return true;
  } catch (error) {
    console.error('❌ Error verifying keypair:', error);
    return false;
  }
};

const e2eEncryptionService = {
  generateUserKeypair,
  storePrivateKeyLocally,
  getPrivateKeyLocally,
  getRecipientPublicKey,
  encryptMessageForRecipient,
  encryptMessageForSelf,
  decryptMessage,
  uploadPublicKeyToFirebase,
  createEncryptedMessagePayload,
  generateMessageHMAC,
  testE2EEncryption,
  verifyKeypairIntegrity,
};

export default e2eEncryptionService;
